import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';

import { Loader, usePaginatedQuery } from '@playbookux/playbook-web-core';
import {
  Table,
  Text,
  Link,
  TableHeaderSmall,
  CenteredContainer,
  CellBorderRight,
  Modal,
  Input,
  Button,
  Message,
  TableHeaderLarge,
  CellBorderLeft,
  DefaultText,
  Title,
  ButtonEntry,
  UsageHeader,
} from './styles';

import UsageTable from '../../../../company/pages/account/usage/custom-table';
import Entry from './entry';
import { GET_COMPANY_BY_ID, ADD_STORAGE } from '../graphql';
import {
  GET_USAGE_LOGS_BY_COMPANY,
  TOGGLE_HIDE_USAGE_LOG,
} from '../../../../company/pages/account/graphql';
import EditStorageModal from '../edit-storage-modal';
import formatBytes from '../../../../core/utils/format-bytes';

function CustomTable({
  pricing, company, changePrice, deletePrice, loadingDelete, loadingChange, featureFlag,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [entryModalpOpen, setEntryModalpOpen] = useState(false);
  const [modalData, setModalData] = useState({
    price: 0,
    type: '',
    key: '',
  });
  const [showEditStorageModal, setShowEditStorageModal] = useState(false);
  const [storageError, setStorageError] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const { data: companyData } = useQuery(GET_COMPANY_BY_ID, {
    variables: { id: company },
    skip: company === 'default',
  });
  const totalStorage = companyData?.company?.uploadResearchTotalStorage || 0;
  const availableStorage = formatBytes(companyData?.company?.uploadResearchAvailableStorage || 0);

  const [adminAddStorage] = useMutation(ADD_STORAGE, {
    refetchQueries: [
      {
        query: GET_COMPANY_BY_ID,
        variables: { id: company },
      },
    ],
  });

  const handleOpenEditStorageModal = () => {
    setShowEditStorageModal(true);
  };

  const handleCloseEditStorageModal = () => {
    setShowEditStorageModal(false);
    setStorageError(null);
  };

  const handleSubmitStorage = async (companyId, newTotalStorage) => {
    try {
      setStorageLoading(true);
      const currentTotalStorage = totalStorage;
      const storageAmount = newTotalStorage - currentTotalStorage;
      await adminAddStorage({
        variables: {
          companyId,
          storageAmount,
        },
      });
      setStorageLoading(false);
      setShowEditStorageModal(false);
    } catch (err) {
      setStorageError(err);
      setStorageLoading(false);
    }
  };

  const attributes = {
    variables: {
      companyId: company,
      limit: 100,
      updateLimit: 25,
      offset: 0,
      order: 'created_at DESC',
    },
    fetchPolicy: 'network-only',
  };

  const {
    data = {},
    fetchMore,
    hasMore,
    refetch,
  } = usePaginatedQuery(GET_USAGE_LOGS_BY_COMPANY, attributes, { dataField: 'usageLogsByCompany' });
  const { usageLogsByCompany = [] } = data;

  const [toggleHideUsageLog] = useMutation(TOGGLE_HIDE_USAGE_LOG);

  const handleOpenEntryModal = () => setEntryModalpOpen(true);

  const handleCloseEntryModal = () => setEntryModalpOpen(false);

  const handleValueChange = (value) => {
    if (value < 0) {
      setError('Price cannot be negative');
      if (!error) {
        setTimeout(() => {
          setError('');
        }, 5000);
      }
      return;
    }
    setModalData({ ...modalData, price: value });
  };

  const handlePriceChange = (closeModal) => {
    if (!modalData.price) {
      setError('You need to enter a price');
      if (!error) {
        setTimeout(() => {
          setError('');
        }, 5000);
      }
      return;
    }
    const floatPrice = parseFloat(modalData.price);
    changePrice({
      variables: {
        productType: modalData.key,
        companyId: company === 'default' ? null : company,
        price: floatPrice,
      },
    });
    closeModal();
  };

  const handleDeletePrice = (key, companyID) => {
    deletePrice({
      variables: {
        productType: key,
        companyId: companyID,
      },
    });
  };

  const openModal = (price, type, key) => {
    setModalData({ price, type, key });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Table basic="very">
        <Table.Header>
          <Table.Row>
            <TableHeaderSmall>
              <Text.Bold />
            </TableHeaderSmall>
            <TableHeaderLarge>
              <CenteredContainer>
                <Text.Header>Cost per tester</Text.Header>
              </CenteredContainer>
            </TableHeaderLarge>
            <TableHeaderSmall />

          </Table.Row>
        </Table.Header>

        <Table.Body>
          {Object.keys(pricing).map((type) => (
            <Table.Row key={type}>
              <CellBorderRight>
                <CenteredContainer>
                  <Text.Header>{type}</Text.Header>
                </CenteredContainer>
              </CellBorderRight>

              <Table.Cell>
                <CenteredContainer>
                  {!loadingChange && (
                    <>
                      <Text.Header>
                        $
                        {pricing[type].price}
                      </Text.Header>
                      <Link onClick={() => openModal(pricing[type].price, type, pricing[type].key)}>
                        Edit
                      </Link>
                    </>
                  )}
                  {loadingChange && (
                    <Loader data-loader="change-custom-table-loader" />
                  )}
                </CenteredContainer>
              </Table.Cell>
              <CellBorderLeft>

                {pricing[type].company !== 'default' && !loadingDelete && (
                  <CenteredContainer>
                    <Button
                      onClick={() => {
                        handleDeletePrice(pricing[type].key, pricing[type].company);
                      }}
                    >
                      Reset to default
                    </Button>
                  </CenteredContainer>
                )}

                {pricing[type].company === 'default' && (
                  <CenteredContainer>
                    <DefaultText>
                      Default Price
                    </DefaultText>
                  </CenteredContainer>
                )}

                {loadingDelete && (
                  <CenteredContainer>
                    <Loader data-loader="deleting-custom-table-loader" />
                  </CenteredContainer>
                )}
              </CellBorderLeft>

            </Table.Row>
          ))}
          {company !== 'default' && (
            <Table.Row>
              <CellBorderRight>
                <CenteredContainer>
                  <Text.Header>Upload Research Storage Remaining</Text.Header>
                </CenteredContainer>
              </CellBorderRight>
              <Table.Cell>
                <CenteredContainer>
                  <Text.Header>
                    {availableStorage}
                  </Text.Header>
                  <Link onClick={handleOpenEditStorageModal} data-cy-edit-storage>
                    Edit
                  </Link>
                </CenteredContainer>
              </Table.Cell>
              <CellBorderLeft />
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      <>
        {isModalOpen && (
          <Modal
            open={isModalOpen}
            onClose={closeModal}
            size="mini"
          >
            <Modal.Header>
              <Text.Header>{modalData.type}</Text.Header>
            </Modal.Header>
            <Modal.Content>
              <Input
                type="number"
                value={modalData.price}
                onChange={(e) => handleValueChange(e.target.value)}
              />
              <Button
                onClick={() => {
                  handlePriceChange(closeModal);
                }}
              >
                Save
              </Button>
              {error && (
                <Message negative>
                  {error}
                </Message>
              )}
            </Modal.Content>
          </Modal>
        )}
        {showEditStorageModal && companyData && (
          <EditStorageModal
            company={companyData.company}
            error={storageError}
            loading={storageLoading}
            onClose={handleCloseEditStorageModal}
            onSubmit={handleSubmitStorage}
            show={showEditStorageModal}
          />
        )}

        {featureFlag()}

        <UsageHeader>
          <Title as="h2">Work Log</Title>
          {company !== 'default' && (
            <ButtonEntry
              onClick={handleOpenEntryModal}
            >
              New Entry
            </ButtonEntry>
          )}
        </UsageHeader>
        {company !== 'default' && (
          <UsageTable
            usageLogs={usageLogsByCompany}
            fetchMore={fetchMore}
            hasMore={hasMore}
            refetch={refetch}
            toggleHideUsageLog={toggleHideUsageLog}
            admin
          />
        )}
        {entryModalpOpen && (
          <Entry
            onClose={handleCloseEntryModal}
            company={company}
            refetch={refetch}
          />
        )}
      </>
    </>

  );
}

CustomTable.propTypes = {
  pricing: PropTypes.shape({
    Unmoderated: PropTypes.number.isRequired,
    'Moderated 30 min': PropTypes.number.isRequired,
    'Moderated 60 min': PropTypes.number.isRequired,
    'Moderated 90 min': PropTypes.number.isRequired,
    'Moderated 120 min': PropTypes.number.isRequired,
    CardSort: PropTypes.number.isRequired,
    TreeTest: PropTypes.number.isRequired,
    Survey: PropTypes.number.isRequired,
  }).isRequired,
  company: PropTypes.string.isRequired,
  changePrice: PropTypes.func.isRequired,
  deletePrice: PropTypes.func.isRequired,
  loadingDelete: PropTypes.bool.isRequired,
  loadingChange: PropTypes.bool.isRequired,
  featureFlag: PropTypes.node.isRequired,
};

export default CustomTable;
